import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api';
import {
  DEFAULT_THEME,
  FALLBACK_BLOCKS,
  type Storefront,
  type StorefrontBlock,
  type StorefrontTheme,
} from './types';

/**
 * Vzhled vylohy.
 *
 * Nacita se jednou nahore a plati pro celou aplikaci – motiv se propisuje
 * do CSS promennych na <html>, takze na nej dosahnou i stranky, ktere
 * z bloku slozene nejsou (kosik, pokladna).
 */

interface StorefrontState {
  readonly theme: StorefrontTheme;
  readonly blocks: readonly StorefrontBlock[];
  readonly loading: boolean;
  /** Bezi vyloha v nahledu builderu? Tehdy data chodi zpravou, ne z API. */
  readonly isPreview: boolean;
}

const StorefrontContext = createContext<StorefrontState>({
  theme: DEFAULT_THEME,
  blocks: FALLBACK_BLOCKS,
  loading: true,
  isPreview: false,
});

/**
 * Origin administrace. Nahled prijima zpravy jen z nej – bez toho by
 * podobu stranky mohl prepsat kdokoli, kdo vylohu vlozi do sveho ramu.
 */
const ADMIN_ORIGIN = (import.meta.env['VITE_ADMIN_URL'] as string | undefined) ??
  'http://localhost:5173';

const PREVIEW_MESSAGE = 'zooshopik-preview';

function isPreviewMode(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('nahled') === '1';
}

export function StorefrontProvider({ children }: { children: ReactNode }) {
  const preview = useMemo(isPreviewMode, []);
  const [state, setState] = useState<Storefront | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .getStorefront()
      .then((result) => {
        if (alive) setState(result);
      })
      .catch(() => {
        // Vzhled je ozdoba, katalog je obchod. Kdyz se nenacte, jede se
        // dal se zalozni podobou misto bile stranky.
        if (alive) setState({ theme: DEFAULT_THEME, blocks: FALLBACK_BLOCKS });
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  /*
   * Nahled v builderu. Administrace posila rozepsanou podobu stranky pri
   * kazde zmene, takze obsluha vidi vysledek driv, nez ho ulozi.
   */
  useEffect(() => {
    if (!preview) return undefined;

    function onMessage(event: MessageEvent<unknown>) {
      if (event.origin !== ADMIN_ORIGIN) return;
      const data = event.data;
      if (typeof data !== 'object' || data === null) return;
      const message = data as { type?: unknown; payload?: unknown };
      if (message.type !== PREVIEW_MESSAGE) return;

      const payload = message.payload as Partial<Storefront> | undefined;
      if (payload === undefined) return;
      setState({
        theme: { ...DEFAULT_THEME, ...(payload.theme ?? {}) },
        blocks: payload.blocks ?? [],
      });
      setLoading(false);
    }

    window.addEventListener('message', onMessage);
    // Administrace nemusi vedet, kdy je ram nacteny. Ozveme se sami.
    window.parent.postMessage({ type: `${PREVIEW_MESSAGE}-ready` }, ADMIN_ORIGIN);
    return () => window.removeEventListener('message', onMessage);
  }, [preview]);

  const theme = state?.theme ?? DEFAULT_THEME;

  // Motiv se propisuje na <html>, ne do <div> – dosahne tak i na prvky
  // mimo strom aplikace (pozadi body, vyber textu).
  useEffect(() => {
    const root = document.documentElement;
    /*
     * Akcent se pouští do CSS jako ZÁKLAD, ne jako hotová hodnota – stejně
     * jako `--sale-base`. V tmavém režimu z něj stylopis odvodí světlejší
     * odstín (kvůli čitelnosti odkazů a focusu na tmavém podkladu). Kdybychom
     * sem zapsali rovnou `--accent`, inline hodnota by tmavou variantu přebila
     * (inline > @media) a akcent by v tmavém režimu zůstal tmavý.
     */
    root.style.setProperty('--accent-base', theme.accent);
    root.style.setProperty('--accent-text', theme.accentInk);
    root.style.setProperty('--accent-soft', theme.accentSoft);
    /*
     * Barva slev se pousti do CSS jako ZAKLAD, ne jako hotova hodnota.
     * V tmavem rezimu z ni stylopis odvodi svetlejsi odstin – syta cervena
     * na tmavem podkladu nema dost kontrastu. Kdybychom sem zapsali rovnou
     * `--sale`, inline hodnota by tmavou variantu prebila.
     */
    root.style.setProperty('--sale-base', theme.saleColor);
    /*
     * Barva štítku „Novinka" se pouští jako ZÁKLAD (--new-base), stejně jako
     * akcent a sleva. Stylopis z ní v styles.css odvodí hotové --new; kdybychom
     * sem zapsali rovnou --new, inline hodnota by přebila případnou tmavou
     * variantu (inline > @media).
     */
    root.style.setProperty('--new-base', theme.newColor);
    root.style.setProperty('--radius', `${theme.radiusPx}px`);
    root.dataset['density'] = theme.density;
    root.dataset['font'] = theme.font;
    root.dataset['motion'] = theme.motion;
  }, [theme]);

  const value = useMemo<StorefrontState>(
    () => ({
      theme,
      blocks: state?.blocks ?? (loading ? [] : FALLBACK_BLOCKS),
      loading,
      isPreview: preview,
    }),
    [theme, state, loading, preview],
  );

  return <StorefrontContext.Provider value={value}>{children}</StorefrontContext.Provider>;
}

export function useStorefront(): StorefrontState {
  return useContext(StorefrontContext);
}
